import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  FC,
} from 'react';
import * as Apollo from '@apollo/client';
import { QueryResult } from '@apollo/client/react/types/types';
import { OperationVariables } from '@apollo/client';
import { Collapse, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useWithAuthSkip } from '../../auth/AuthenticationContext';
import { useAuthenticator } from '../../auth/AuthenticationContext';
import {
  useScheduledNotificationsQuery,
  useScheduledNotificationsSubscriptionSubscription,
  useMarkSystemNotificationSeenMutation,
  useMarkSystemNotificationAcknowledgedMutation,
} from '../../../types/generated-types';
import { Log, LogCategory } from './logger';
import { handleModelUpdateEvent } from '../../../helpers/subscriptionUtils';

type SystemNotificationType = NonNullable<
  ReturnType<typeof useScheduledNotificationsQuery>['data']
>['systemNotifications'][number];

export interface IStatusNotifier {
  message?: string;
  systemNotificationMessage?: string;
  showNextButton: boolean;
  showRespondButton: boolean;
  currentNotificationLink?: string;
  manageAsyncOp(
    opName: string,
    inProgress: boolean,
    inProgressMessage?: string,
    transitionMessage?: string | undefined,
    transitionDelayMs?: number | undefined,
  ): void;
  handleNextNotification(): void;
  handleRespondClick(): void;
  //refreshNotifications(): void;
}

const StatusNotifierContext = createContext<IStatusNotifier>({
  message: 'test message',
  systemNotificationMessage: undefined,
  showNextButton: false,
  showRespondButton: false,
  currentNotificationLink: undefined,
  manageAsyncOp: (
    opName: string,
    inProgress: boolean,
    inProgressMessage?: string,
    transitionMessage?: string | undefined,
    transitionDelayMs?: number | undefined,
  ) => {
    console.log(
      opName,
      inProgress,
      inProgressMessage,
      transitionMessage,
      transitionDelayMs,
    );
  },
  handleNextNotification: () => {},
  handleRespondClick: () => {},
  // refreshNotifications: () => {},
});

StatusNotifierContext.displayName = 'StatusNotifierContext';

interface StatusManagerProviderProps {
  children: React.ReactNode;
}

export const StatusNotifierProvider: FC<StatusManagerProviderProps> = ({
  children,
}) => {
  const { activeSession, user } = useAuthenticator();
  const navigate = useNavigate();
  const [message, setMessage] = useState<string | undefined>();
  const [systemNotificationMessage, setSystemNotificationMessage] = useState<
    string | undefined
  >(undefined);
  const [showNextButton, setShowNextButton] = useState(false);
  const [showRespondButton, setShowRespondButton] = useState(false);
  const [currentNotificationLink, setCurrentNotificationLink] = useState<
    string | undefined
  >(undefined);
  const [currentNotificationIndex, setCurrentNotificationIndex] = useState(-1);
  const [currentNotification, setCurrentNotification] =
    useState<SystemNotificationType | null>(null);
  // Mutations
  const [markSystemNotificationSeen] = useMarkSystemNotificationSeenMutation();
  const [markSystemNotificationAcknowledged] =
    useMarkSystemNotificationAcknowledgedMutation();

  const [filteredNotifications, setFilteredNotifications] = useState<
    SystemNotificationType[]
  >([]);

  // Query for scheduled notifications
  const {
    data: scheduledNotificationsData,
    refetch: refetchScheduledNotifications,
  } = useScheduledNotificationsQuery({
    skip: !activeSession || !user,
    fetchPolicy: 'network-only',
  });

  // Subscription for real-time updates
  useScheduledNotificationsSubscriptionSubscription({
    skip: !activeSession || !user,
    fetchPolicy: 'no-cache',
    onData: handleModelUpdateEvent(async ({ data }) => {
      if (data?.data?.systemNotificationEvents) {
        console.log(
          'StatusNotifier: System notification event detected, re-fetching notifications',
        );
        // Refetch the scheduled notifications query to get fresh data
        // The useEffect will handle refreshing the UI when the data changes
        await refetchScheduledNotifications();
      } else {
        console.log('No event detected');
      }
    }),
  });

  useEffect(() => {
    let myFilteredNotifications: SystemNotificationType[] = [];
    if (
      scheduledNotificationsData?.systemNotifications &&
      activeSession &&
      user
    ) {
      const notifications = scheduledNotificationsData.systemNotifications;
      const currentTimestamp = Date.now();

      myFilteredNotifications = notifications.filter((notification) => {
        // Filter out expired notifications
        if (notification.displayEnd <= currentTimestamp) {
          return false;
        }

        // Filter out acknowledged notifications (for acknowledgement required ones)
        if (
          notification.requireAcknowledgement &&
          notification.acknowledgedAt &&
          notification.acknowledgedAt !== 1
        ) {
          return false;
        }

        // Filter out non-acknowledgement-required notifications already seen by current user
        const hasSeen = notification.seenBy?.some(
          (seen) => seen?.userId === user._id,
        );
        return !hasSeen;
      });
    }
    setFilteredNotifications(myFilteredNotifications);
    setCurrentNotificationIndex(0);
    setCurrentNotification(myFilteredNotifications[0]);
  }, [activeSession, scheduledNotificationsData, user]);

  // Get filtered notifications on demand
  // const getFilteredNotifications = useCallback(() => {
  //   if (
  //     !activeSession ||
  //     !user ||
  //     !scheduledNotificationsData?.systemNotifications
  //   ) {
  //     return [];
  //   }
  //
  //   const notifications =
  //     scheduledNotificationsData.systemNotifications as SystemNotification[];
  //   const currentTimestamp = Date.now();
  //
  //   return notifications.filter((notification) => {
  //     // Filter out expired notifications
  //     if (notification.displayEnd <= currentTimestamp) {
  //       return false;
  //     }
  //
  //     // Filter out acknowledged notifications (for acknowledgement required ones)
  //     if (notification.requireAcknowledgement && notification.acknowledgedAt) {
  //       return false;
  //     }
  //
  //     // Filter out non-acknowledgement-required notifications already seen by current user
  //     const hasSeen = notification.seenBy?.some(
  //       (seen) => seen?.userId === user._id,
  //     );
  //     if (hasSeen) {
  //       return false;
  //     }
  //
  //     return true;
  //   });
  // }, [activeSession, user, scheduledNotificationsData]);

  // Display a specific notification
  const displayNotification = useCallback(
    (notification: SystemNotificationType) => {
      if (!notification || !user) return;

      setSystemNotificationMessage(notification.message);
      setCurrentNotificationLink(notification.link || undefined);
      setShowNextButton(true);
      setShowRespondButton(!!notification.link);

      // Mark as seen if not already seen by current user
      const hasSeen = (notification.seenBy ?? [])?.length > 0;
      if (!hasSeen) {
        markSystemNotificationSeen({
          variables: {
            id: notification._id,
          },
        }).catch((error: any) => {
          Log.error(
            'StatusNotifier: Failed to mark notification as seen',
            { id: notification._id, error: error.message },
            LogCategory.DATA,
          );
        });
      }
    },
    [markSystemNotificationSeen, user],
  );

  // Refresh notifications and restart display
  // const refreshNotifications = useCallback(() => {
  //   console.log('StatusNotifier: refreshNotifications called');
  //   console.log(
  //     'StatusNotifier: filtered notifications:',
  //     filteredNotifications,
  //   );
  //
  //   if (filteredNotifications.length === 0) {
  //     // No notifications to show
  //     setSystemNotificationMessage(undefined);
  //     setShowNextButton(false);
  //     setShowRespondButton(false);
  //     setCurrentNotificationLink(undefined);
  //     setCurrentNotificationIndex(0);
  //     return;
  //   }
  //
  //   // Reset to first notification and start display
  //   setCurrentNotificationIndex(0);
  //   displayNotification(filteredNotifications[0]);
  // }, [filteredNotifications, displayNotification]);

  // Handle next button click
  const handleNextClick = useCallback(async () => {
    if (filteredNotifications.length === 0) {
      setSystemNotificationMessage(undefined);
      setShowNextButton(false);
      setShowRespondButton(false);
      setCurrentNotificationLink(undefined);
      setCurrentNotificationIndex(0);
      return;
    }

    if (currentNotification) {
      if (currentNotification.requireAcknowledgement) {
        await markSystemNotificationAcknowledged({
          variables: { id: currentNotification._id },
        });
      }
      await markSystemNotificationSeen({
        variables: { id: currentNotification._id },
      });
    }

    const nextIndex = currentNotificationIndex + 1;
    const hasNext = nextIndex < filteredNotifications.length;
    const nextNotification = hasNext ? filteredNotifications[nextIndex] : null;

    if (!nextNotification) {
      // All notifications displayed, start over
      setCurrentNotificationIndex(0);
      setCurrentNotification(null);
      displayNotification(filteredNotifications[0]);
    } else {
      // Show next notification
      setCurrentNotificationIndex(nextIndex);
      setCurrentNotification(nextNotification);
      displayNotification(nextNotification);
    }
  }, [
    filteredNotifications,
    currentNotification,
    currentNotificationIndex,
    markSystemNotificationSeen,
    markSystemNotificationAcknowledged,
    displayNotification,
  ]);

  // Handle respond button click
  const handleRespondClick = useCallback(async () => {
    if (filteredNotifications.length === 0) {
      setSystemNotificationMessage(undefined);
      setShowNextButton(false);
      setShowRespondButton(false);
      setCurrentNotificationLink(undefined);
      setCurrentNotificationIndex(0);
      return;
    }

    if (currentNotification) {
      if (currentNotification.requireAcknowledgement) {
        await markSystemNotificationAcknowledged({
          variables: { id: currentNotification._id },
        });
      }
      await markSystemNotificationSeen({
        variables: { id: currentNotification._id },
      });
      if (currentNotificationLink) {
        setTimeout(() => {
          try {
            const url = new URL(currentNotificationLink);
            const path = url.pathname + url.search;
            navigate(path);
          } catch (error) {
            Log.error(
              'StatusNotifier: Invalid notification link',
              { link: currentNotificationLink },
              LogCategory.DATA,
            );
          }
        }, 500);
      }
    }
  }, [
    currentNotification,
    currentNotificationLink,
    filteredNotifications.length,
    markSystemNotificationAcknowledged,
    markSystemNotificationSeen,
    navigate,
  ]);

  // Initialize notifications when data becomes available
  // useEffect(() => {
  //   if (
  //     activeSession &&
  //     user &&
  //     scheduledNotificationsData?.systemNotifications
  //   ) {
  //     refreshNotifications();
  //   }
  // }, [activeSession, user, scheduledNotificationsData, refreshNotifications]);

  // Manage async operations
  const manageAsyncOp = useCallback(
    (
      opName: string,
      inProgress: boolean,
      inProgressMessage = 'Loading ...',
      transitionMessage: string | undefined = undefined,
      transitionDelayMs: number | undefined = undefined,
    ) => {
      if (inProgress) {
        setMessage(inProgressMessage);
      } else {
        if (transitionMessage) {
          setMessage(transitionMessage);
          setTimeout(() => {
            setMessage(undefined);
          }, transitionDelayMs || 1000);
        } else {
          setMessage(undefined);
        }
      }
    },
    [],
  );

  // Context value
  const value: IStatusNotifier = useMemo(() => {
    return {
      message,
      systemNotificationMessage,
      showNextButton,
      showRespondButton,
      currentNotificationLink,
      manageAsyncOp,
      handleNextNotification: handleNextClick,
      handleRespondClick,
      // currentNotification,
      // refreshNotifications,
    };
  }, [
    message,
    systemNotificationMessage,
    showNextButton,
    showRespondButton,
    currentNotificationLink,
    manageAsyncOp,
    handleNextClick,
    handleRespondClick,
    // currentNotification,
    // refreshNotifications,
  ]);

  return (
    <StatusNotifierContext.Provider value={value}>
      {children}
    </StatusNotifierContext.Provider>
  );
};

export function useStatusNotifier(): IStatusNotifier {
  const context = useContext(StatusNotifierContext);
  if (context === undefined) {
    throw new Error(
      'useStatusNotifier must be used within a StatusNotifierProvider',
    );
  }
  return context;
}

export function useQueryHook<TData, TQueryVariables extends OperationVariables>(
  queryHook: (
    baseOptions: Apollo.QueryHookOptions<TData, TQueryVariables>,
  ) => QueryResult<TData, TQueryVariables>,
  queryHookOptions: Apollo.QueryHookOptions<TData, TQueryVariables> = {},
  loadingMessage = 'Loading ...',
  renderingMessage: string | undefined = undefined,
  renderingDelay: number | undefined = undefined,
): QueryResult<TData, TQueryVariables> {
  const finalOptions = useWithAuthSkip(queryHookOptions);
  const res = queryHook(finalOptions);
  const { loading } = res;
  const { manageAsyncOp } = useStatusNotifier();

  useEffect(() => {
    manageAsyncOp(
      'propertiesList',
      loading,
      loadingMessage,
      !loading ? renderingMessage : undefined,
      !loading ? renderingDelay : undefined,
    );
  }, [
    loading,
    loadingMessage,
    manageAsyncOp,
    renderingMessage,
    renderingDelay,
  ]);

  return res;
}

export const StatusMessagePanel = () => {
  const {
    message,
    systemNotificationMessage,
    showNextButton,
    showRespondButton,
    handleNextNotification,
    handleRespondClick,
  } = useStatusNotifier();

  return (
    <>
      {/* Original status message slot */}
      <Collapse orientation="vertical" in={!!message}>
        <div
          style={{
            border: '2px solid orange',
            borderTop: '2px solid red',
            height: 'auto',
            textAlign: 'center',
            fontSize: '20px',
          }}
        >
          {message}
        </div>
      </Collapse>

      {/* System notification message slot */}
      <Collapse orientation="vertical" in={!!systemNotificationMessage}>
        <div
          style={{
            border: '2px solid orange',
            borderTop: '3px solid orange',
            borderRight: '4px solid orange',
            height: 'auto',
            textAlign: 'center',
            fontSize: '20px',
            backgroundColor: '#e3f2fd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
          }}
        >
          <span style={{ flex: 1 }}>{systemNotificationMessage}</span>
          {showNextButton && (
            <Button
              variant={showRespondButton ? 'outlined' : 'contained'}
              size="small"
              onClick={handleNextNotification}
              style={{
                minWidth: '60px',
                height: '24px',
                fontSize: '12px',
                marginLeft: '16px',
              }}
            >
              {showRespondButton ? 'Skip' : 'Next'}
            </Button>
          )}
          {showRespondButton && (
            <Button
              variant="contained"
              size="small"
              onClick={handleRespondClick}
              style={{
                minWidth: '70px',
                height: '24px',
                fontSize: '12px',
                marginLeft: '16px',
              }}
            >
              Respond
            </Button>
          )}
        </div>
      </Collapse>
    </>
  );
};
